<template>
    <app-layout>
        <template #title>Dashboard</template>
        <template #description>Platform overview and analytics</template>

        <KioskNav />

        <div class="max-w-7xl px-4 py-8 sm:px-6 lg:px-8 mx-auto">
            <!-- Donut Charts -->
            <div class="gap-6 lg:grid-cols-2 grid">
                <!-- Verification Donut -->
                <div
                    class="bg-white rounded-xl border-iron-200 overflow-hidden border"
                >
                    <div class="px-5 py-4">
                        <p
                            class="font-medium text-iron-400 tracking-wider mb-4 text-[11px] uppercase"
                        >
                            Verification Status
                        </p>
                        <DonutChart
                            :data="verificationDonutChartData"
                            :index="'name'"
                            :category="'total'"
                        />
                    </div>
                </div>

                <!-- Language Donut -->
                <div
                    class="bg-white rounded-xl border-iron-200 overflow-hidden border"
                >
                    <div class="px-5 py-4">
                        <p
                            class="font-medium text-iron-400 tracking-wider mb-4 text-[11px] uppercase"
                        >
                            Language Usage
                        </p>
                        <DonutChart
                            :data="languageDonutChartData"
                            :index="'name'"
                            :category="'total'"
                        />
                    </div>
                </div>
            </div>

            <!-- Signups by Month Bar Chart -->
            <div class="mt-8">
                <div class="gap-3 mb-4 flex items-center">
                    <h2 class="text-lg font-semibold text-iron-900">
                        Signups by Month
                    </h2>
                    <span
                        class="px-2.5 py-0.5 text-xs font-medium bg-iron-100 text-iron-600 rounded-full"
                    >
                        Last 12 Months
                    </span>
                </div>

                <Deferred data="userStats">
                    <template #fallback>
                        <div
                            class="bg-white rounded-xl border-iron-200 overflow-hidden border"
                        >
                            <div class="p-6">
                                <div
                                    class="bg-iron-100 rounded-xl animate-pulse h-[350px]"
                                ></div>
                            </div>
                        </div>
                    </template>

                    <div
                        class="bg-white rounded-xl border-iron-200 overflow-hidden border"
                    >
                        <div class="p-6">
                            <BarChart
                                :data="userStats"
                                index="name"
                                :categories="[
                                    'total',
                                    'verified',
                                    'with_reservations',
                                    'total_reservations',
                                ]"
                                :colors="[
                                    '#84ADFF',
                                    '#7CD4FD',
                                    '#10B981',
                                    '#F59E0B',
                                ]"
                                :y-formatter="
                                    (tick: number) => {
                                        return String(tick);
                                    }
                                "
                                :custom-tooltip="BarChartTooltip"
                            />
                        </div>
                    </div>
                </Deferred>
            </div>
        </div>
    </app-layout>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Deferred } from "@inertiajs/vue3";
import AppLayout from "../../../../Layouts/AppLayout.vue";
import KioskNav from "../components/KioskNav.vue";
import { DonutChart } from "@/components/ui/chart-donut";
import { BarChart } from "@/components/ui/chart-bar";
import BarChartTooltip from "../Users/components/BarChartTooltip.vue";

type UserStats = {
    total: number;
    name: string;
    verified: number;
    with_reservations: number;
    total_reservations: number;
};

type UserRow = {
    id: number;
    email_verified_at: string | null;
    lang: string | null;
};

const props = defineProps<{
    userStats: UserStats[];
    users: UserRow[];
}>();

const verificationDonutChartData = computed(() => {
    if (!props.users || props.users.length === 0) return [];
    const verified = props.users.filter(
        (u) => u.email_verified_at !== null,
    ).length;
    const unverified = props.users.filter(
        (u) => u.email_verified_at === null,
    ).length;
    return [
        { total: verified, name: "verified" },
        { total: unverified, name: "unverified" },
    ];
});

const languageDonutChartData = computed(() => {
    if (!props.users || props.users.length === 0) return [];
    const numberOfCs = props.users.filter((u) => u.lang === "cs").length;
    const numberOfEn = props.users.filter((u) => u.lang === "en").length;
    return [
        { total: numberOfCs, name: "cs" },
        { total: numberOfEn, name: "en" },
    ];
});
</script>
